<template>
  <div class="auth-page md-layout md-alignment-center-center">

    <form novalidate
          class="auth-page__form
                 md-layout
                 md-layout-item
                 md-alignment-center-center"
          @submit.prevent="submit">

      <md-card
        class="auth-page__card
               md-layout-item
               md-size-30
               md-large-size-45
               md-medium-size-55
               md-small-size-75
               md-xsmall-size-100">
        <md-progress-bar md-mode="indeterminate" v-if="isPending"/>

        <md-card-header>
          <div class="text_xl_large">{{ i18n.log_signin() }}</div>
        </md-card-header>

        <md-card-content>
          <input-field
            v-if="form.email.trim().length === 0 && verifyEmailParams && verifyEmailParams.email.length > 0"
            class="input-field"
            id="login-email"
            v-model="verifyEmailParams.email"
            :label="i18n.lbl_email()"
            name="email"
            :errorMessage="errorMessage('email')"
                       v-validate="'required'"
          />
          <input-field
            v-else class="input-field"
            id="login-email"
            v-model="form.email"
            :label="i18n.lbl_email()"
            name="email"
            :errorMessage="errorMessage('email')"
            :autocomplete="true"
            v-validate="'required'"
          />
          <input-field
            class="input-field"
            id="login-password"
            v-model.trim="form.password"
            type="password"
            :togglePassword="true"
            :label="i18n.lbl_pwd()"
            name="password"
            :errorMessage="errorMessage('password')"
            :data-vv-as="i18n.signup_password()"
            :autocomplete="true"
            v-validate="'required'"
          />

          <div class="auth-page__bottom">
            <div class="auth-page__tips">
              <div class="tips__tip text_small">
                {{ i18n.log_dont_have_an_account() }}
                <router-link :to="{ name: 'signup' }">{{ i18n.log_register_now() }}</router-link>
              </div>
              <div class="tips__tip text_small">
                {{ i18n.log_forgot_password() }}
                <router-link :to="{ name: 'recovery' }">{{ i18n.log_recover_it() }}</router-link>
              </div>
            </div>

          </div>

          <div class="btn-wrap margin-top">
            <md-button type="submit" class="md-primary btn md-yellow w-100" :disabled="isPending">{{ i18n.log_signin() }}</md-button>
          </div>

          <Tfa v-if="config.FEATURE_FLAGS.tfa"
               @enterApplication="enterApplication"
               class="security-settings__tfa-settings"
               ref="qrForm"
          />
        </md-card-content>
      </md-card>
    </form>
  </div>
</template>

<script>
  import { vuexTypes } from '@vuex/types'
  import { vueRoutes } from '@router/const'
  import {mapActions, mapGetters} from 'vuex'

  import formMixin from '@com/mixins/form.mixin'

  import {showAlert} from '@js/modals/alert_message'
  import { confirmAction } from '@js/modals/confirmation_message'

  import {errors} from '@js/errors/factory'
  import {EventDispatcher} from '@js/events/event_dispatcher'
  import {ErrorHandler} from '@js/errors/error_handler'
  import {dispatchAppEvent} from '@js/events/helpers'
  import {commonEvents} from '@js/events/common_events'

  import { WalletHelper } from '@js/helpers/wallet.helper'
  import { walletService } from '@api/wallet.service'
  import { emailService } from '@api/email.service'
  import { usersService } from '@api/users.service'
  import Tfa from './TwoFA'

  import { i18n } from '@js/i18n'
  import config from '@conf'
  import axios from 'axios'

  export default {
    mixins: [formMixin],
    components: {
      Tfa
    },
    data: () => ({
      config,
      callQR: false,
      isAuth: false,
      form: {
        password: '',
        email: ''
      },
      i18n,
      hasSeenWarning: null
    }),

    created () {
      if (this.verifyEmailParams) {
        return this.verifyEmail()
      }
      this.hasSeenWarning = localStorage.hasOwnProperty('seenBlockWarning')
    },

    computed: {
      ...mapGetters([
        vuexTypes.masterAccountId,
        vuexTypes.accountId,
        vuexTypes.accountType,
        vuexTypes.accountBlocked,
        vuexTypes.accountSeed
      ]),
      verifyEmailParams () {
        const token = this.$route.params.token
        const walletId = this.$route.params.walletId
        const email = this.$route.params.email
        if (token && walletId) {
          return { token, walletId, email }
        }
        return null
      }
    },

    methods: {
      ...mapActions({
        setUserLoggedIn: vuexTypes.LOG_IN, // TODO
        getAMLMessages: 'getAMLMessages',
        getExistMessages: 'getExistMessages',
        processUserWallet: vuexTypes.PROCESS_USER_WALLET,
        loadConfig: vuexTypes.GET_SERVER_CONFIG,
        loadMaster: vuexTypes.GET_MASTER_ACCOUNT_ID,
        loadAccount: vuexTypes.GET_ACCOUNT_DETAILS,
        loadAccountKyc: vuexTypes.GET_ACCOUNT_KYC_REQUESTS,
        loadUser: vuexTypes.GET_USER_DETAILS,
        loadBalances: vuexTypes.GET_ACCOUNT_BALANCES,
        loadSigner: vuexTypes.GET_ACCOUNT_SIGNER,
        loadTaurus: vuexTypes.GET_USER_TAURUS,
        loadUplataInsha: vuexTypes.GET_USER_UPLATA_INSHA,
        loadOperatonalAccountId: vuexTypes.GET_OPERATIONAL_ACCOUNT_ID,
        setApproverData: vuexTypes.setApproverData
      }),
      async submit () {
        if (!await this.isValid()) return
        this.form.email = this.form.email.toLowerCase()
        this.form.email = this.form.email.trim()
        this.disable()

        try {
          await this.loadConfig()
          await this.processUserWallet(this.form)
          if (!await usersService.checkIfUserExists()) {
            await usersService.createUser(this.accountId)
          }
          await this.fetchUserDetails()
          await this.$refs.qrForm.checkState()
          await this.loadAccountKyc()
          const { data } = await axios.post(`${process.env.USDT_LISTENER_SERVER}/auth/login`, {seed: this.accountSeed})
          sessionStorage.setItem('token', data.token)
          // eslint-disable-next-line
          this.setApproverData({couldDelegate: data.active, sign_key: data.sign_key, 
            // eslint-disable-next-line
            approval_level: data.approval_level, role: data.role, token: data.token})
          await this.getExistMessages()
          this.getAMLMessages()
        } catch (error) {
          switch (error.constructor) {
            case errors.NotFoundError:
              error.showBanner(i18n.not_found())
              break
            case errors.EmailNotVerifiedError:
              this.handleNotVerifiedError()
              break
            default:
              ErrorHandler.processUnexpected(error)
          }
        }

        this.enable()
      },

      async fetchUserDetails () {
        await this.loadConfig()
        await Promise.all([
          this.loadMaster(),
          this.loadUser(),
          this.loadAccount(),
          this.loadBalances(),
          this.loadSigner(this.masterAccountId),
          this.loadTaurus(),
          this.loadUplataInsha(),
          this.loadOperatonalAccountId()
        ])
      },

      async enterApplication () {
        this.setUserLoggedIn()
        dispatchAppEvent(commonEvents.enterAppEvent)
        this.$router.push(vueRoutes.app)

        if (this.accountBlocked) {
          if (!this.hasSeenWarning) {
            localStorage.setItem('seenBlockWarning', 'User acknowledged about block')
            this.hasSeenWarning = true
            showAlert({
              title: i18n.mod_accountBlocked_title(),
              message: i18n.mod_accountBlocked_message()
            })
          }
        }
        await this.loadAccountKyc()
      },
      // TODO: wtf, need drop this away
      async handleNotVerifiedError () {
        if (!await confirmAction({
          title: i18n.mod_email_not_verified_title(),
          message: i18n.mod_email_not_verified_message()
        })) return
        this.disable()
        try {
          const kdf = await walletService.loadKdfParamsForEmail(this.form.email)
          const { walletId } = WalletHelper.calculateWalletParams(
            this.form.password,
            this.form.email,
            kdf.attributes().salt,
            kdf.attributes()
          )
          await emailService.sendResendEmailRequest(walletId)
          EventDispatcher.dispatchShowSuccessEvent(i18n.email_sent())
        } catch (error) {
          ErrorHandler.processUnexpected(error)
        }
        this.enable()
      },

      async verifyEmail () {
        try {
          await emailService.sendVerifyEmailRequest(this.verifyEmailParams.token, this.verifyEmailParams.walletId)
          EventDispatcher.dispatchShowSuccessEvent(i18n.email_verified())
          this.form.email = this.verifyEmailParams.email
        } catch (error) {
          console.error(error)
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import 'auth';
  .w-100 {
    width: 100%;
  }
  .tips__tip {
    a {
      color: $default-black;
      font-weight: bold;
      text-decoration: underline;
    }
  }
</style>



// WEBPACK FOOTER //
// src/vue/auth/Login.vue